/**
 * Standard fee schedule — Compliance/Admin management screen (#17, Inc A).
 *
 * The firm-level Standard fee scale (a TRANCHES schedule). Read mode shows the
 * current active version + the version history (read-only — versions are
 * immutable, superseded never edited). Editing authors a NEW active version
 * (POST /fee-schedules/standard) behind a confirm modal; the prior version is
 * superseded. Direct write, audited (gated fee_schedule.standard.manage).
 *
 * IMPORTANT (decision D1, fees-tab spec): agreements that already adopted the
 * Standard hold a frozen COPY (crystallized) — publishing a new version here
 * does NOT change their fees; only NEW adoptions copy this version.
 *
 * Band editor uses DERIVED contiguity: each row's lower bound is the previous
 * row's upper bound (0 for the first); the last row is always open-top (∞). So
 * the band set is contiguous-by-construction — the user only sets the upper
 * thresholds + each band's rate, never an invalid/overlapping range.
 */
import { useMemo, useState } from 'react';
import {
  ActionIcon, Alert, Button, Container, Group, Modal, NumberInput, Stack,
  Table, Text, TextInput, Title, Tooltip,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
  IconAlertTriangle, IconArrowBackUp, IconPencil, IconPlus, IconScale, IconTrash,
} from '@tabler/icons-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { ApiError } from '../api/client';
import {
  bpsToPct, centsToEur, eurToCents, feeSchedulesApi, fmtEurFromCents,
  pctToBps, type FeeScheduleOut, type FeeTrancheIn,
} from '../api/feeSchedules';
import { RequireAccess } from '../components/RequireAccess';
import { BackToCompliance } from '../components/BackToCompliance';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { useDirtyFormGuard } from '../hooks/useDirtyFormGuard';

const HISTORY_KEY = ['fee-schedules', 'standard-history'] as const;

const WARNING =
  'Publishing a new Standard fee schedule supersedes the current version and ' +
  'becomes the firm default offered when an agreement chooses "Standard". ' +
  'Agreements that have ALREADY adopted the Standard keep their fixed copy and ' +
  'are unaffected; only new adoptions copy this version.';

interface BandDraft { rowKey: string; upTo: number | null; rate: number }

/** Map a saved schedule's tranches → editor rows (€ + %, last = open-top). */
function scheduleToBands(s: FeeScheduleOut | null): BandDraft[] {
  if (!s || s.kind !== 'tranches' || s.tranches.length === 0) {
    return [{ rowKey: 'b0', upTo: null, rate: 0 }];  // a single open-top band
  }
  return s.tranches
    .slice()
    .sort((a, b) => a.seq - b.seq)
    .map((t, i) => ({
      rowKey: `b${i}`,
      upTo: t.max_eur_cents === null ? null : centsToEur(t.max_eur_cents),
      rate: bpsToPct(t.rate_bps),
    }));
}

/** Lower bound (€) of row i = the previous row's upper bound (0 for the first). */
function lowerOf(rows: BandDraft[], i: number): number {
  return i === 0 ? 0 : (rows[i - 1].upTo ?? 0);
}

export function StandardFeeSchedulePage() {
  useDocumentTitle('Standard fee schedule');
  return (
    <RequireAccess permission="fee_schedule.standard.manage">
      <Container size="xl">
        <Stack gap="xl">
          <BackToCompliance to="/admin" label="Back to Admin" />
          <StandardEditor />
        </Stack>
      </Container>
    </RequireAccess>
  );
}

export function StandardEditor() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: HISTORY_KEY, queryFn: feeSchedulesApi.standardHistory });
  const history = q.data ?? [];
  const current = useMemo(() => history.find((s) => s.status === 'active') ?? null, [history]);

  const [editMode, setEditMode] = useState(false);
  const [bands, setBands] = useState<BandDraft[]>([]);
  const [effFrom, setEffFrom] = useState('');
  const [seq, setSeq] = useState(0);
  const [saving, setSaving] = useState(false);
  const [warn, setWarn] = useState(false);

  // Dirty = the drafted version differs from the current active one (or there
  // is no current). Authoring an identical version would just be a no-op.
  const dirty = useMemo(() => {
    if (!editMode) return false;
    const cur = scheduleToBands(current);
    const sameBands = bands.length === cur.length
      && bands.every((b, i) => b.upTo === cur[i].upTo && b.rate === cur[i].rate);
    const sameEff = effFrom === (current?.effective_from ?? '');
    return !(sameBands && sameEff);
  }, [editMode, bands, effFrom, current]);
  const confirmDiscard = useDirtyFormGuard(dirty);

  const enter = () => {
    setBands(scheduleToBands(current));
    setEffFrom(current?.effective_from ?? '');
    setEditMode(true);
  };
  const leave = () => { setEditMode(false); setBands([]); setEffFrom(''); };

  const patch = (k: string, p: Partial<BandDraft>) =>
    setBands((d) => d.map((r) => (r.rowKey === k ? { ...r, ...p } : r)));
  const addBand = () => {
    // Append a new open-top band. The previously-last row becomes a middle
    // (bounded) row whose upper threshold the user must now set — validation
    // flags it until they do. Immutable update (no in-place mutation).
    setSeq((n) => n + 1);
    setBands((d) => [...d, { rowKey: `nb-${seq}`, upTo: null, rate: 0 }]);
  };
  const removeBand = (k: string) =>
    setBands((d) => {
      if (d.length <= 1) return d;       // always keep at least one band
      const filtered = d.filter((r) => r.rowKey !== k);
      // The new last row is open-top.
      return filtered.map((r, idx) => (idx === filtered.length - 1 ? { ...r, upTo: null } : r));
    });

  function validate(): string | null {
    if (bands.length === 0) return 'At least one band is required.';
    for (let i = 0; i < bands.length - 1; i++) {
      const lo = lowerOf(bands, i);
      const up = bands[i].upTo;
      if (up === null || up === undefined || Number.isNaN(up)) return `Band ${i + 1} needs an upper threshold (€).`;
      if (up <= lo) return `Band ${i + 1}: "up to" (€${up.toLocaleString()}) must be greater than €${lo.toLocaleString()}.`;
    }
    for (const b of bands) if (b.rate < 0 || b.rate > 100) return 'Each rate must be between 0 and 100 %.';
    return null;
  }

  function buildBands(): FeeTrancheIn[] {
    return bands.map((b, i) => ({
      min_eur_cents: eurToCents(lowerOf(bands, i)),
      max_eur_cents: i === bands.length - 1 ? null : eurToCents(b.upTo as number),
      rate_bps: pctToBps(b.rate),
    }));
  }

  async function save() {
    setWarn(false); setSaving(true);
    try {
      await feeSchedulesApi.createStandard({
        kind: 'tranches', bands: buildBands(), effective_from: effFrom || null,
      });
      await qc.invalidateQueries({ queryKey: HISTORY_KEY });
      notifications.show({ color: 'green', message: 'Standard fee schedule published' });
      leave();
    } catch (e) {
      notifications.show({
        color: 'red', title: 'Could not publish', autoClose: false,
        message: e instanceof ApiError ? e.detail : 'Unknown error',
      });
    } finally { setSaving(false); }
  }
  function onSave() {
    const err = validate();
    if (err) { notifications.show({ color: 'red', title: 'Cannot save', message: err }); return; }
    if (!dirty) { leave(); return; }
    setWarn(true);
  }

  return (
    <Stack>
      <Group justify="space-between">
        <Group gap="xs"><IconScale size={20} /><Title order={3}>Standard fee schedule</Title></Group>
        {!editMode ? (
          <Button leftSection={<IconPencil size={16} />} onClick={enter}>
            {current ? 'New version' : 'Define standard'}
          </Button>
        ) : (
          <Group gap="xs">
            <Button variant="default" leftSection={<IconArrowBackUp size={16} />}
              onClick={() => confirmDiscard(leave)}>Discard</Button>
            <Button variant="light" leftSection={<IconPlus size={16} />} onClick={addBand}>Add band</Button>
            <Button color="azul" loading={saving} onClick={onSave}>Save changes</Button>
          </Group>
        )}
      </Group>
      <Text c="dimmed" size="sm">
        The firm's progressive (marginal) fee scale offered when an agreement chooses
        “Standard”. Each band's rate applies to the portion of AuM within it. The last
        band is open-top. Editing publishes a new version; existing agreements keep
        their fixed copy.
      </Text>
      {q.isError && <Text c="red">Failed to load the standard fee schedule.</Text>}

      {/* ── current / draft band table ──────────────────────────────── */}
      {!editMode && !current && (
        <Alert color="gray" variant="light">No standard fee schedule defined yet.</Alert>
      )}
      {(editMode || current) && (
        <Table withTableBorder>
          <Table.Thead><Table.Tr>
            <Table.Th>Band (AuM)</Table.Th>
            <Table.Th w={200}>Up to (€)</Table.Th>
            <Table.Th w={140}>Rate (%/yr)</Table.Th>
            {editMode && <Table.Th w={60} />}
          </Table.Tr></Table.Thead>
          <Table.Tbody>
            {(editMode ? bands : scheduleToBands(current)).map((r, i, arr) => {
              const lo = lowerOf(arr, i);
              const isLast = i === arr.length - 1;
              return (
                <Table.Tr key={r.rowKey}>
                  <Table.Td>
                    <Text size="sm">
                      €{lo.toLocaleString()} {isLast ? 'and above' : `– €${(r.upTo ?? 0).toLocaleString()}`}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    {editMode && !isLast
                      ? <NumberInput size="xs" min={0} thousandSeparator value={r.upTo ?? ''}
                          onChange={(v) => patch(r.rowKey, { upTo: typeof v === 'number' ? v : null })} />
                      : <Text c="dimmed">{isLast ? '∞' : `€${(r.upTo ?? 0).toLocaleString()}`}</Text>}
                  </Table.Td>
                  <Table.Td>
                    {editMode
                      ? <NumberInput size="xs" min={0} max={100} step={0.01} decimalScale={2} suffix=" %"
                          value={r.rate}
                          onChange={(v) => patch(r.rowKey, { rate: typeof v === 'number' ? v : 0 })} />
                      : <Text>{r.rate.toFixed(2)} %</Text>}
                  </Table.Td>
                  {editMode && (
                    <Table.Td>
                      <Tooltip label={arr.length <= 1 ? 'At least one band is required' : 'Remove band'} withArrow>
                        <ActionIcon variant="subtle" color="red" disabled={arr.length <= 1}
                          onClick={() => removeBand(r.rowKey)} aria-label="Remove band">
                          <IconTrash size={16} />
                        </ActionIcon>
                      </Tooltip>
                    </Table.Td>
                  )}
                </Table.Tr>
              );
            })}
          </Table.Tbody>
        </Table>
      )}

      {editMode && (
        <Group align="flex-end">
          <TextInput type="date" label="Effective from (optional)" value={effFrom}
            onChange={(e) => setEffFrom(e.currentTarget.value)} w={220} />
        </Group>
      )}

      {/* ── version history ─────────────────────────────────────────── */}
      {history.length > 0 && (
        <Stack gap="xs" mt="md">
          <Title order={5}>Version history</Title>
          <Table withTableBorder striped>
            <Table.Thead><Table.Tr>
              <Table.Th w={70}>Version</Table.Th>
              <Table.Th w={110}>Status</Table.Th>
              <Table.Th w={140}>Effective from</Table.Th>
              <Table.Th>Bands</Table.Th>
            </Table.Tr></Table.Thead>
            <Table.Tbody>
              {history.map((s) => (
                <Table.Tr key={s.id}>
                  <Table.Td>v{s.version}</Table.Td>
                  <Table.Td>
                    <Text size="sm" c={s.status === 'active' ? 'verde.7' : 'dimmed'}
                      fw={s.status === 'active' ? 600 : 400}>{s.status}</Text>
                  </Table.Td>
                  <Table.Td><Text size="sm" ff="monospace">{s.effective_from ?? '—'}</Text></Table.Td>
                  <Table.Td><Text size="sm" c="dimmed">{bandsSummary(s)}</Text></Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Stack>
      )}

      <Modal opened={warn} onClose={() => setWarn(false)} centered
        title={<Text fw={600}>Publish standard fee schedule</Text>}>
        <Stack>
          <Alert color="orange" icon={<IconAlertTriangle size={16} />} title="Firm-wide change">{WARNING}</Alert>
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setWarn(false)}>Cancel</Button>
            <Button color="azul" loading={saving} onClick={save}>Publish new version</Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}

/** One-line band summary for the history table. */
function bandsSummary(s: FeeScheduleOut): string {
  if (s.kind !== 'tranches') return s.kind;
  return s.tranches
    .slice()
    .sort((a, b) => a.seq - b.seq)
    .map((t) => {
      const lo = fmtEurFromCents(t.min_eur_cents);
      const hi = t.max_eur_cents === null ? '∞' : fmtEurFromCents(t.max_eur_cents);
      return `${lo}–${hi}: ${bpsToPct(t.rate_bps).toFixed(2)}%`;
    })
    .join('  ·  ');
}
